/**
 * useConnections — Manages friend/family connections including requests, acceptance, and removal.
 */
import { useCallback, useEffect } from 'react';
import { ConnectionState, useConnectionsState } from '@/hooks/connections/useConnectionsState';
import { useConnectionQueries } from '@/hooks/connections/useConnectionQueries';
import { useConnectionMutations } from '@/hooks/connections/useConnectionMutations';
import { useAuth } from '@/hooks/useAuth';
import { Connection, PhoneConnectionResult } from '@/types/connections';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

export function useConnections() {
  // Get state and state setters
  const { 
    connections, 
    setConnections,
    pendingConnections, 
    setPending, 
    loading, 
    setLoading, 
    error, 
    setError 
  } = useConnectionsState();
  
  // Get query functions
  const { fetchConnections } = useConnectionQueries();
  
  // Get mutation functions
  const { 
    sendConnectionRequest: sendConnectionRequestBase, 
    acceptConnection: acceptConnectionBase, 
    rejectConnection: rejectConnectionBase, 
    removeConnection: removeConnectionBase,
    sendPhoneConnectionRequest 
  } = useConnectionMutations();
  
  const { user } = useAuth();
  const { toast } = useToast();

  // Wrapper for fetchConnections that updates state
  const fetchAndUpdateConnections = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await fetchConnections();
      
      if ('error' in result) {
        setError(result.error);
        toast({
          title: "Error loading connections",
          description: result.error,
          variant: "destructive",
        });
      } else {
        // Ensure the connections conform to the Connection type
        setConnections(result.acceptedConnections as Connection[]);
        setPending(result.pendingConnections as Connection[]);
        setError(null);
      }
    } catch (error: any) {
      logger.error("Error fetching connections:", error);
      setError(error.message);
      toast({
        title: "Error loading connections",
        description: error.message || "Failed to load connections",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user, fetchConnections, setConnections, setPending, setLoading, setError, toast]);

  // Enhanced versions of mutation functions that automatically refresh connections
  const sendConnectionRequest = useCallback(async (receiverId: string) => {
    try {
      const result = await sendConnectionRequestBase(receiverId);
      
      if (result) {
        toast({
          title: "Success",
          description: "Connection request sent successfully",
          variant: "default",
        });
        await fetchAndUpdateConnections();
      }
      
      return result;
    } catch (error: any) {
      logger.error("Error sending connection request:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send connection request",
        variant: "destructive",
      });
      return false;
    }
  }, [sendConnectionRequestBase, fetchAndUpdateConnections, toast]);

  const acceptConnection = useCallback(async (connectionId: string) => {
    try {
      const result = await acceptConnectionBase(connectionId);
      
      if (result) {
        await fetchAndUpdateConnections();
      }
      
      return result;
    } catch (error: any) {
      logger.error("Error accepting connection:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to accept connection",
        variant: "destructive",
      });
      return false;
    }
  }, [acceptConnectionBase, fetchAndUpdateConnections, toast]);

  const rejectConnection = useCallback(async (connectionId: string) => {
    try {
      const result = await rejectConnectionBase(connectionId);
      
      if (result) {
        await fetchAndUpdateConnections();
      }
      
      return result;
    } catch (error: any) {
      logger.error("Error rejecting connection:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject connection",
        variant: "destructive",
      });
      return false;
    }
  }, [rejectConnectionBase, fetchAndUpdateConnections, toast]);

  const removeConnection = useCallback(async (connectionId: string) => {
    try {
      const result = await removeConnectionBase(connectionId);
      
      if (result) {
        await fetchAndUpdateConnections();
      }
      
      return result;
    } catch (error: any) {
      logger.error("Error removing connection:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove connection",
        variant: "destructive",
      });
      return false;
    }
  }, [removeConnectionBase, fetchAndUpdateConnections, toast]);
  
  // Enhanced version of sendPhoneConnectionRequest that refreshes connections
  const sendPhoneConnectionRequestWithRefresh = useCallback(async (
    phoneData: { countryCode: string; phoneNumber: string }, 
    preferredName?: string
  ): Promise<PhoneConnectionResult> => {
    const result = await sendPhoneConnectionRequest(phoneData, preferredName);
    
    if (result.success) {
      await fetchAndUpdateConnections();
    }
    
    return result;
  }, [sendPhoneConnectionRequest, fetchAndUpdateConnections]);

  // Fetch connections on initial load and when user changes
  useEffect(() => {
    if (user) {
      fetchAndUpdateConnections();
    }
  }, [user, fetchAndUpdateConnections]);

  return {
    connections,
    pendingConnections,
    loading,
    error,
    fetchConnections: fetchAndUpdateConnections,
    sendConnectionRequest,
    sendPhoneConnectionRequest: sendPhoneConnectionRequestWithRefresh,
    acceptConnection,
    rejectConnection,
    removeConnection
  };
}
